import React, { useEffect } from "react";
import { Controller, useWatch } from "react-hook-form";
import { Trash2 } from "lucide-react";
import { TextField, SwitchField } from "../../../shared/components/ui";
import InputMascarado from "../../../shared/components/formulario/input-mascarado/InputMascarado";
import { mascaraMoedaReal } from "../../../shared/utils/mascaras/mascaraMoedaReal";
import { SelectLaboBudgetConfig } from "../../../shared/components/formulario/input-select-labo-budget-config/selectLaboBudgetConfig";
import { SelectUnitOfMeasure } from "../../../shared/components/formulario/select/SelectUnitOfMeasure";

// Utilitário para converter string monetária mascarada para número
function parseCurrencyToNumber(valor: string | number): number {
    if (typeof valor === 'number') return valor;
    if (!valor) return 0;
    // Remove tudo que não for dígito ou vírgula
    let cleaned = valor.replace(/[^\d,]/g, '');
    // Troca a última vírgula por ponto (caso exista)
    const lastComma = cleaned.lastIndexOf(',');
    if (lastComma !== -1) {
        cleaned =
            cleaned.slice(0, lastComma).replace(/,/g, '') +
            '.' +
            cleaned.slice(lastComma + 1);
    }
    return Number(cleaned) || 0;
}

// Utilitário para formatar valores monetários
function formatCurrency(value: number, currency: string = 'BRL', locale: string = 'pt-BR') {
    return value.toLocaleString(locale, { style: 'currency', currency });
}

interface ItemEditorProps {
    stepIndex: number;
    itemIndex: number;
    methods: any;
    removeItem: (index: number) => void;
    disabled?: boolean;
}

export function ItemEditor({ stepIndex, itemIndex, methods, removeItem, disabled }: ItemEditorProps) {
    // Garante que a lista de mão de obra esteja disponível no form
    // Adiciona tratamento para idProfitMargin
    const namePrefix = `steps.${stepIndex}.itens.${itemIndex}`;
    const { control, setValue } = methods;

    React.useEffect(() => {
        if (!methods.getValues('laborsConfig') || methods.getValues('laborsConfig').length === 0) {
            // Tenta buscar do localStorage (ou outro local, se necessário)
            const laborsFromStorage = localStorage.getItem('laborsConfig');
            if (laborsFromStorage) {
                try {
                    methods.setValue('laborsConfig', JSON.parse(laborsFromStorage));
                } catch { }
            }
        }
    }, [methods]);

    const isLabor = useWatch({ control, name: `${namePrefix}.isLabor` });
    const selectedLabor = useWatch({ control, name: `${namePrefix}.idLaborBudget` });
    const quantity = useWatch({ control, name: `${namePrefix}.quantity` }) || 0;
    const unitValueField = useWatch({ control, name: `${namePrefix}.unitValue` }) || 0;

    useEffect(() => {
        if (isLabor && selectedLabor) {
            let laborObj = selectedLabor;
            // Se selectedLabor for apenas o id, tenta buscar o objeto completo
            if (typeof selectedLabor === 'string' && methods?.getValues) {
                const allLabors = methods.getValues('laborsConfig') || [];
                laborObj = allLabors.find((l: any) => l.id === selectedLabor) || selectedLabor;
            }
            if (laborObj && typeof laborObj === 'object') {
                setValue(`${namePrefix}.unitOfMeasure`, laborObj.unitOfMeasure || "");
                setValue(`${namePrefix}.unitValue`, laborObj.wage);
                setValue(`${namePrefix}.description`, laborObj.description);
                setValue(`${namePrefix}.idLaborBudget`, laborObj.id); // garante que só o id vai para o form
            }
        }
    }, [isLabor, selectedLabor, setValue, namePrefix, methods]);

    // Garante que o valor unitário seja numérico, mesmo vindo mascarado
    const valorUnitario = parseCurrencyToNumber(isLabor ? (selectedLabor?.wage ?? unitValueField) : unitValueField);
    const total = Number(quantity || 0) * valorUnitario;

    return (
        <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40 sm:flex-row sm:flex-wrap sm:items-end">
            {/* Mão de obra */}
            <div className="flex shrink-0 flex-col items-center justify-center gap-1 sm:w-24">
                <span className="text-center text-xs font-medium text-slate-500 dark:text-slate-400">Mão de obra</span>
                <Controller
                    name={`${namePrefix}.isLabor`}
                    control={control}
                    defaultValue={false}
                    disabled={disabled}
                    render={({ field }) => (
                        <SwitchField
                            checked={field.value || false}
                            onChange={field.onChange}
                            disabled={disabled}
                        />
                    )}
                />
            </div>

            {/* Descrição ou Select mão de obra */}
            <div className="min-w-[200px] flex-1 sm:basis-72">
                {isLabor ? (
                    <SelectLaboBudgetConfig
                        name={`${namePrefix}.idLaborBudget`}
                        methods={methods}
                        disabled={disabled}
                        onChangeExtra={selected => {
                            if (selected && typeof selected === 'object') {
                                setValue(`${namePrefix}.unitOfMeasure`, selected.unitOfMeasure || "");
                                setValue(`${namePrefix}.unitValue`, selected.wage);
                                setValue(`${namePrefix}.description`, selected.description);
                                setValue(`${namePrefix}.idLaborBudget`, selected.id);
                            }
                        }}
                    />
                ) : (
                    <Controller
                        name={`${namePrefix}.description`}
                        control={control}
                        disabled={disabled}
                        defaultValue={""}
                        render={({ field }) => <TextField {...field} label="Descrição" />}
                    />
                )}
            </div>

            {/* Quantidade */}
            <div className="w-24 shrink-0">
                <Controller
                    name={`${namePrefix}.quantity`}
                    control={control}
                    disabled={disabled}
                    defaultValue={1}
                    render={({ field }) => <TextField {...field} label="Qtd" type="number" />}
                />
            </div>

            {/* Valor unitário */}
            <div className="w-32 shrink-0">
                {isLabor ? (
                    <Controller
                        name={`${namePrefix}.unitValue`}
                        control={control}
                        disabled={disabled}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                value={field.value ? formatCurrency(Number(field.value)) : ""}
                                label="Valor unit."
                                disabled
                            />
                        )}
                    />
                ) : (
                    <InputMascarado
                        name={`${namePrefix}.unitValue`}
                        methods={methods}
                        disabled={disabled}
                        label="Valor"
                        mask={mascaraMoedaReal}
                    />
                )}
            </div>

            {/* Unidade de medida */}
            <div className="w-36 shrink-0">
                <SelectUnitOfMeasure name={`${namePrefix}.unitOfMeasure`} control={control} disabled={disabled} />
            </div>

            {/* Total */}
            <div className="flex w-28 shrink-0 flex-col items-start justify-center gap-1 sm:items-center">
                <span className="text-xs font-medium text-slate-500 dark:text-slate-400 sm:hidden">Total</span>
                <span className="whitespace-nowrap font-semibold text-slate-700 dark:text-slate-200">
                    {formatCurrency(total)}
                </span>
            </div>

            {/* Botão deletar */}
            <div className="flex shrink-0 items-center justify-center sm:pb-2">
                <button
                    type="button"
                    aria-label="Excluir item"
                    disabled={disabled}
                    onClick={() => removeItem(itemIndex)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full text-red-500 transition-colors hover:bg-red-50 hover:text-red-700 disabled:pointer-events-none disabled:opacity-40 dark:text-red-400 dark:hover:bg-red-900/30 dark:hover:text-red-300"
                >
                    <Trash2 className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}
